import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../api";
import { toast } from "sonner";
import { ArrowLeft, Upload, Pencil, Check, X } from "lucide-react";

const PALETTE = [
  "#FF5733", "#FFC300", "#9CCC65", "#42A5F5",
  "#E76F51", "#F4A261", "#2A9D8F", "#264653",
  "#E63946", "#A8DADC", "#BDB2FF", "#FFB5A7",
];

export default function ChildrenPage() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/children");
      setChildren(res.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateColor = async (id, color) => {
    try {
      const res = await api.put(`/children/${id}`, { color });
      setChildren((prev) => prev.map((c) => (c.id === id ? res.data : c)));
      toast.success("Couleur mise à jour");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const updateName = async (id, name) => {
    try {
      const res = await api.put(`/children/${id}`, { name });
      setChildren((prev) => prev.map((c) => (c.id === id ? res.data : c)));
      toast.success("Prénom mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const uploadAvatar = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/children/${id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setChildren((prev) => prev.map((c) => (c.id === id ? res.data : c)));
      toast.success("Avatar mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'upload");
    }
  };

  return (
    <div className="min-h-screen paper-bg" data-testid="children-page">
      <header className="border-b-[1.5px] border-[var(--ink)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-5 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="pill flex items-center gap-2"
            data-testid="back-to-feed-button"
          >
            <ArrowLeft size={14} /> retour
          </button>
          <h1 className="font-serif-display text-3xl sm:text-4xl italic">les enfants</h1>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
        {loading ? (
          <div className="py-24 text-center font-serif-display text-2xl italic text-[var(--ink-soft)]">
            chargement…
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {children.map((c) => (
              <ChildCard
                key={c.id}
                child={c}
                onColor={(col) => updateColor(c.id, col)}
                onAvatar={(f) => uploadAvatar(c.id, f)}
                onRename={(n) => updateName(c.id, n)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function ChildCard({ child, onColor, onAvatar, onRename }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(child.name);
  const avatar = child.avatar_path ? fileUrl(child.avatar_path) : null;
  const initial = child.name.charAt(0).toUpperCase();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    await onAvatar(f);
    setUploading(false);
    e.target.value = "";
  };

  const saveName = async () => {
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === child.name) {
      setEditing(false);
      setDraftName(child.name);
      return;
    }
    await onRename(trimmed);
    setEditing(false);
  };

  const cancelName = () => {
    setDraftName(child.name);
    setEditing(false);
  };

  return (
    <div
      className="card-scrap p-6 relative"
      style={{ boxShadow: `6px 6px 0 0 ${child.color}` }}
      data-testid={`child-card-${child.name.toLowerCase()}`}
    >
      <div className="flex items-center gap-5">
        <div
          className="relative w-24 h-24 rounded-full border-[1.5px] border-[var(--ink)] overflow-hidden flex items-center justify-center shrink-0"
          style={{ background: child.color }}
        >
          {avatar ? (
            <img src={avatar} alt={child.name} className="w-full h-full object-cover" />
          ) : (
            <span className="font-serif-display text-5xl italic">{initial}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveName();
                  if (e.key === "Escape") cancelName();
                }}
                autoFocus
                className="bg-transparent border-b-2 border-[var(--ink)] font-serif-display italic text-3xl outline-none py-1 w-full max-w-[180px]"
                data-testid={`rename-input-${child.name.toLowerCase()}`}
              />
              <button
                onClick={saveName}
                className="p-1.5 rounded-full border-[1.5px] border-[var(--ink)] hover:bg-[var(--ink)] hover:text-white"
                data-testid={`rename-save-${child.name.toLowerCase()}`}
                title="Valider"
              >
                <Check size={14} />
              </button>
              <button
                onClick={cancelName}
                className="p-1.5 rounded-full border-[1.5px] border-[var(--ink)] hover:bg-[var(--kid-levy)] hover:text-white"
                data-testid={`rename-cancel-${child.name.toLowerCase()}`}
                title="Annuler"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="font-serif-display text-3xl italic">{child.name}</div>
              <button
                onClick={() => {
                  setDraftName(child.name);
                  setEditing(true);
                }}
                className="p-1.5 rounded-full hover:bg-[var(--ink)] hover:text-white transition-colors"
                data-testid={`rename-btn-${child.name.toLowerCase()}`}
                title="Renommer"
              >
                <Pencil size={13} />
              </button>
            </div>
          )}
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="mt-2 pill inline-flex items-center gap-2"
            data-testid={`upload-avatar-${child.name.toLowerCase()}`}
          >
            <Upload size={13} />
            {uploading ? "envoi…" : avatar ? "changer l'avatar" : "ajouter un avatar"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
          />
        </div>
      </div>

      <div className="mt-6">
        <div className="font-ui text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)] mb-2">
          couleur signature
        </div>
        <div className="flex flex-wrap gap-2">
          {PALETTE.map((col) => (
            <button
              key={col}
              onClick={() => onColor(col)}
              className={`w-8 h-8 rounded-full border-[1.5px] transition-transform hover:-translate-y-0.5 ${
                child.color.toLowerCase() === col.toLowerCase()
                  ? "border-[var(--ink)] ring-2 ring-[var(--ink)] ring-offset-2 ring-offset-[var(--bg)]"
                  : "border-[var(--ink)]"
              }`}
              style={{ background: col }}
              title={col}
              data-testid={`color-${child.name.toLowerCase()}-${col.replace("#", "")}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
